import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query';
import {
    AskQuestionResponse,
    Question,
    QuestionListResponse,
    QuestionByIdResponse,
    GetUsageResponse,
} from '../../types';
import { selectors, store } from '../../../user-area/redux/store';

const tpcQuestionApi = createApi({
    reducerPath: 'tpcQuestionApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '/api/tpc/index.php',
    }),
    endpoints: (builder) => ({
        getPastCorrections: builder.query<
            QuestionListResponse,
            {
                cursor: string | null;
                isTutor: boolean;
            }
        >({
            query: ({ cursor, isTutor }) => {
                const { token, uuid: user_id } = selectors.auth.credentials(store.getState().auth);
                return {
                    url: '',
                    method: 'POST',
                    params: {
                        action: 'get-questions',
                        token,
                        uuid: user_id,
                        cursor: cursor?.toString(),
                        is_tutor: isTutor,
                    },
                    headers: {
                        Accept: '*/*',
                        'Content-Type': 'application/json',
                    },
                };
            },
            serializeQueryArgs: ({ endpointName }) => endpointName, // Ensures query is merged
            merge: (currentCache, newItems) => {
                currentCache.data.questions.push(...newItems.data.questions);
                currentCache.data.is_last_page = newItems.data.is_last_page;
                currentCache.data.next_cursor = newItems.data.next_cursor;
            },
            forceRefetch({ currentArg, previousArg }) {
                return currentArg?.cursor !== previousArg?.cursor;
            },
        }),
        getQuestionByUuid: builder.query<
            QuestionByIdResponse,
            {
                question_uuid: string;
                isTutor: boolean;
            }
        >({
            query: ({ question_uuid, isTutor }) => {
                const { token, uuid: user_id } = selectors.auth.credentials(store.getState().auth);
                return {
                    url: '',
                    method: 'POST',
                    params: {
                        action: 'get-question-by-uuid',
                        token,
                        uuid: user_id,
                        question_uuid: question_uuid.toString(),
                        is_tutor: isTutor,
                    },
                    headers: {
                        Accept: '*/*',
                        'Content-Type': 'application/json',
                    },
                };
            },
            forceRefetch() {
                return true;
            },
        }),
        askQuestion: builder.query<
            AskQuestionResponse,
            {
                question: string;
                image: string | null;
                isTutor: boolean;
            }
        >({
            query: ({ question, image, isTutor }) => {
                const { token, uuid: user_id } = selectors.auth.credentials(store.getState().auth);
                const formData = new FormData();
                formData.append('action', 'ask-question');
                formData.append('token', token);
                formData.append('uuid', user_id);
                formData.append('question', question);
                formData.append('is_tutor', isTutor.toString());
                if (image) {
                    formData.append('image', image);
                }
                return {
                    url: '',
                    method: 'POST',
                    body: formData,
                    headers: {
                        Accept: '*/*',
                    },
                };
            },
            forceRefetch() {
                return true;
            },
        }),
        sendFeedback: builder.query<
            void,
            {
                feedback: string;
                type: string;
                tpc_uuid: string;
                isTutor: boolean;
            }
        >({
            query: ({ feedback, type, tpc_uuid, isTutor }) => {
                const { token, uuid: user_id } = selectors.auth.credentials(store.getState().auth);
                return {
                    url: '',
                    method: 'POST',
                    params: {
                        action: 'feedback',
                        token,
                        uuid: user_id,
                        feedback,
                        type,
                        tpc_uuid,
                        is_tutor: isTutor,
                    },
                };
            },
        }),
        getUsage: builder.query<GetUsageResponse, {
            isTutor: boolean;
        }>({
            query: ({ isTutor }) => {
                const { token, uuid: user_id, } = selectors.auth.credentials(store.getState().auth);
                return {
                    url: '',
                    method: 'POST',
                    params: {
                        action: 'get-usage',
                        token,
                        uuid: user_id,
                        is_tutor: isTutor,
                    },
                };
            },
            forceRefetch() {
                return true;
            },
        }),
    }),
});

export default tpcQuestionApi;
